import { useCallback, useEffect, useState } from "react";

export function useStorage<T>(
  key: string,
  type: "local" | "session",
): [T | null, (v: T) => void, { isLoading: boolean }] {
  const storage: Storage | undefined =
    type === "local" ? window.localStorage : window.sessionStorage;

  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState<T | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setItem is declared below this effect, so listing it is a TDZ error (tsc TS2448). It is only reached in the JSON.parse failure path, and storage is window.localStorage/sessionStorage whose methods never change identity.
  useEffect(() => {
    const item = storage?.getItem(key);

    try {
      const parsedItem = item ? JSON.parse(item) : null;
      setValue(parsedItem);
    } catch (e) {
      console.warn(e);
      setValue(null);
      setItem(null);
    }

    setIsLoading(false);
  }, [key]);

  const setItem = useCallback(
    (v: T | null) => {
      if (v == null || v === "undefined" || v === "null") {
        storage?.removeItem(key);
        setValue(null);
      } else {
        storage?.setItem(key, JSON.stringify(v));
        setValue(v);
      }
    },
    [key, storage?.setItem, storage?.removeItem],
  );

  return [value, setItem, { isLoading }];
}
